import { useState, useEffect } from 'react';
import { FileText, Calendar, Award, BookOpen, User } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState({
    student: {},
    exam_results: 0,
    exam_average: 0,
    exam_performance: { total: 0, excellent: 0, good: 0, satisfactory: 0, poor: 0 },
    attendance: { total_sessions: 0, present_sessions: 0, attendance_percentage: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/student/dashboard');
      if (res.data.success && res.data.data && res.data.data.dashboard) {
        setDashboard(res.data.data.dashboard);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwords.new !== passwords.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwords.new.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      const res = await api.put('/student/password', {
        current_password: passwords.current,
        new_password: passwords.new
      });
      if (res.data.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswords({ current: '', new: '', confirm: '' });
        setTimeout(() => setShowPasswordModal(false), 2000);
      }
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    }
  };

  const attendanceData = [
    { name: 'Present', value: Number(dashboard.attendance.present_sessions) || 0, color: '#10b981' },
    { name: 'Absent', value: Number(dashboard.attendance.total_sessions - dashboard.attendance.present_sessions) || 0, color: '#ef4444' },
  ];
  const resultsData = [
    { name: 'Excellent (75+)', value: dashboard.exam_performance.excellent || 0, color: '#10b981' },
    { name: 'Good (60-74)', value: dashboard.exam_performance.good || 0, color: '#3b82f6' },
    { name: 'Satisfactory (50-59)', value: dashboard.exam_performance.satisfactory || 0, color: '#f59e0b' },
    { name: 'Poor (<50)', value: dashboard.exam_performance.poor || 0, color: '#ef4444' },
  ];

  const getAttendanceStatus = (pct) => {
    if (pct >= 90) return { label: 'Excellent', color: 'text-green-600' };
    if (pct >= 75) return { label: 'Good', color: 'text-blue-600' };
    if (pct >= 60) return { label: 'Average', color: 'text-yellow-600' };
    return { label: 'Poor', color: 'text-red-600' };
  };

  const attendanceStatus = getAttendanceStatus(dashboard.attendance.attendance_percentage || 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  // If dashboard data is missing or API failed, show a friendly error
  if (!dashboard || typeof dashboard !== 'object' || !('student' in dashboard)) {
    return <div className="flex items-center justify-center h-64 text-red-500 font-semibold">Failed to load dashboard data. Please try again later.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome, {dashboard.student.full_name || user?.fullName}!</h1>
        <p className="text-indigo-100 mt-1">{dashboard.student.grade_name} - {dashboard.student.email}</p>
        <button onClick={() => navigate('/student/profile')} className="mt-4 flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition text-sm">
          <User className="w-4 h-4" /> View Profile
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-500 truncate">Exam Results</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800 mt-2">{dashboard.exam_results}</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1 truncate">Total exams taken</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-xl flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-500 truncate">Average Score</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800 mt-2">{dashboard.exam_average}%</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1 truncate">Across all exams</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-xl flex-shrink-0">
              <Award className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-500 truncate">Attendance</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800 mt-2">{dashboard.attendance.attendance_percentage}%</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1 truncate">{dashboard.attendance.present_sessions}/{dashboard.attendance.total_sessions} sessions</p>
            </div>
            <div className="p-3 bg-green-100 rounded-xl flex-shrink-0">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Attendance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Attendance Overview</h3>
          {dashboard.attendance && dashboard.attendance.total_sessions > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={attendanceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {attendanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-4">
                <p className="text-3xl font-bold text-gray-800">{dashboard.attendance.attendance_percentage}%</p>
                <p className={`text-sm font-medium ${attendanceStatus.color}`}>{attendanceStatus.label} Attendance</p>
              </div>
              <div className="flex justify-center gap-6 mt-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-600">Present: {dashboard.attendance.present_sessions}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-600">Absent: {(dashboard.attendance.total_sessions - dashboard.attendance.present_sessions)}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">
              No attendance data yet
            </div>
          )}
        </div>

        {/* Results Overview Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Results Overview</h3>
          {dashboard.exam_performance && dashboard.exam_performance.total > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={resultsData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" radius={[8, 8, 0, 0]}>
                    {resultsData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">Excellent: {dashboard.exam_performance.excellent}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-gray-600">Good: {dashboard.exam_performance.good}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-gray-600">Satisfactory: {dashboard.exam_performance.satisfactory}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-gray-600">Poor: {dashboard.exam_performance.poor}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">
              No exam results yet
            </div>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Links</h3>
        <div className="space-y-3">
          <a href="/student/results" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 transition">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">View Exam Results</p>
              <p className="text-sm text-gray-500">Check your marks and component scores</p>
            </div>
          </a>
          <a href="/student/attendance" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 transition">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">View Attendance</p>
              <p className="text-sm text-gray-500">Check your attendance history</p>
            </div>
          </a>
          <a href="/student/subjects" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 transition">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">My Subjects</p>
              <p className="text-sm text-gray-500">View your enrolled subjects</p>
            </div>
          </a>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPasswordModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h2>

            {passwordError && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{passwordError}</div>}
            {passwordSuccess && <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm">{passwordSuccess}</div>}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input type="password" required value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input type="password" required value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input type="password" required value={passwords.confirm} onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
